/* Hedera storefront behaviour — consolidated 31 Jul 2026. */
(() => {
  'use strict';

  const mobileDrawerQuery = window.matchMedia('(max-width: 989px)');
  const drawerOpenClass = 'hedera-mobile-menu-open';

  const drawerRoots = (root = document) => Array.from(root.querySelectorAll('header-drawer'));
  const drawerDetails = (drawer) => drawer.querySelector('#Details-menu-drawer-container') || drawer.querySelector(':scope > details');

  const syncDrawerState = () => {
    const open = mobileDrawerQuery.matches && drawerRoots().some((drawer) => {
      const details = drawerDetails(drawer);
      return details?.open && details.classList.contains('menu-opening');
    });
    document.body.classList.toggle(drawerOpenClass, open);
  };

  const resetRestoredDrawer = (drawer) => {
    const details = drawerDetails(drawer);
    if (!details || !details.open) return;

    details.querySelectorAll('details').forEach((nested) => {
      nested.open = false;
      nested.classList.remove('menu-opening');
      nested.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'false');
    });
    details.querySelectorAll('.submenu-open').forEach((element) => element.classList.remove('submenu-open'));
    details.open = false;
    details.classList.remove('menu-opening');
    details.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'false');
    drawer.closest('.section-header')?.classList.remove('menu-open');

    const breakpoint = drawer.dataset.breakpoint;
    if (breakpoint) document.body.classList.remove(`overflow-hidden-${breakpoint}`);
  };

  const bindDrawer = (drawer) => {
    if (drawer.dataset.hederaStateBound === 'true') return;
    const details = drawerDetails(drawer);
    if (!details) return;
    drawer.dataset.hederaStateBound = 'true';
    details.addEventListener('toggle', syncDrawerState);

    /* Shopify leaves the details element open briefly during its close animation.
       Watch the menu-opening class as well so our backdrop/header state clears
       the instant closing starts rather than waiting for the delayed open removal. */
    const stateObserver = new MutationObserver(syncDrawerState);
    stateObserver.observe(details, { attributes: true, attributeFilter: ['class', 'open'] });
  };

  const bindDesktopMenus = (root = document) => {
    const desktopQuery = window.matchMedia('(min-width: 990px)');
    root.querySelectorAll('.header__inline-menu details').forEach((details) => {
      if (details.dataset.hederaHoverReady === 'true') return;
      details.dataset.hederaHoverReady = 'true';
      let closeTimer;

      const cancelClose = () => window.clearTimeout(closeTimer);
      const closeOthers = () => {
        document.querySelectorAll('.header__inline-menu details[open]').forEach((item) => {
          if (item !== details) item.removeAttribute('open');
        });
      };
      const open = () => {
        if (!desktopQuery.matches) return;
        cancelClose();
        closeOthers();
        details.setAttribute('open', '');
      };
      const close = (delay) => {
        if (!desktopQuery.matches) return;
        cancelClose();
        closeTimer = window.setTimeout(() => details.removeAttribute('open'), delay);
      };

      details.addEventListener('mouseenter', open);
      details.addEventListener('mouseleave', () => close(140));
      details.addEventListener('focusin', open);
      details.addEventListener('focusout', (event) => {
        if (!details.contains(event.relatedTarget)) close(100);
      });
    });
  };

  const bindCore = (root = document) => {
    drawerRoots(root).forEach(bindDrawer);
    bindDesktopMenus(root);
    syncDrawerState();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => bindCore(), { once: true });
  } else {
    bindCore();
  }

  document.addEventListener('shopify:section:load', (event) => bindCore(event.target));
  mobileDrawerQuery.addEventListener?.('change', syncDrawerState);

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) drawerRoots().forEach(resetRestoredDrawer);
    document.body.classList.remove(drawerOpenClass);
    bindCore();
  });
})();


(() => {
    const SCROLL_POINT = 70;

    const initialise = () => {
      const header = document.querySelector('.header-wrapper.hedera-header--overlay');
      if (!header || header.dataset.hederaScrollReady === 'true') return;

      header.dataset.hederaScrollReady = 'true';
      const update = () => header.classList.toggle('hedera-header--scrolled', window.scrollY >= SCROLL_POINT);

      update();
      window.addEventListener('scroll', update, { passive: true });
      window.addEventListener('load', update, { once: true });
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initialise, { once: true });
    } else {
      initialise();
    }

    document.addEventListener('shopify:section:load', initialise);
  })();

(() => {
    const mobileQuery = window.matchMedia('(max-width: 749px)');
    let frozenTop = null;
    let globalListenersBound = false;

    const searchDetails = () =>
      Array.from(document.querySelectorAll('.header details-modal.header__search > details'));

    const isAnySearchOpen = () => searchDetails().some((details) => details.open);

    const measureSearchTop = () => {
      const headerWrapper = document.querySelector('.section-header .header-wrapper');
      if (!headerWrapper) return 0;
      return Math.max(0, Math.round(headerWrapper.getBoundingClientRect().top));
    };

    const applyPlaceholder = () => {
      document.querySelectorAll('.header details-modal.header__search .search__input').forEach((input) => {
        if (!input.dataset.desktopPlaceholder) {
          input.dataset.desktopPlaceholder = input.getAttribute('placeholder') || 'Search';
        }
        input.setAttribute(
          'placeholder',
          mobileQuery.matches ? 'Search our store' : input.dataset.desktopPlaceholder
        );
      });
    };

    const syncOpenState = ({ allowMeasure = false } = {}) => {
      applyPlaceholder();

      if (!mobileQuery.matches) {
        frozenTop = null;
        document.body.classList.remove('hedera-mobile-search-active');
        document.documentElement.style.removeProperty('--hedera-mobile-search-top');
        return;
      }

      const open = isAnySearchOpen();
      document.body.classList.toggle('hedera-mobile-search-active', open);

      if (!open) {
        frozenTop = null;
        return;
      }

      // Freeze this value for the whole open session. iPhone keyboard resize
      // events must not recalculate it and move the bar or close button.
      if (frozenTop === null || allowMeasure) frozenTop = measureSearchTop();
      document.documentElement.style.setProperty('--hedera-mobile-search-top', `${frozenTop}px`);
    };

    const bind = () => {
      applyPlaceholder();

      searchDetails().forEach((details) => {
        if (details.dataset.hederaMobileSearchBound === 'true') return;
        details.dataset.hederaMobileSearchBound = 'true';

        const summary = details.querySelector(':scope > summary');
        summary?.addEventListener('pointerdown', () => {
          if (mobileQuery.matches && !details.open) frozenTop = measureSearchTop();
        }, { passive: true });

        details.addEventListener('toggle', () => {
          requestAnimationFrame(() => syncOpenState());
        });
      });

      if (!globalListenersBound) {
        globalListenersBound = true;

        // Normal desktop/window resizes can update the closed-state position.
        // While search is open, ignore keyboard-driven viewport resizes.
        window.addEventListener('resize', () => {
          if (!isAnySearchOpen()) syncOpenState({ allowMeasure: true });
        }, { passive: true });

        window.addEventListener('orientationchange', () => {
          frozenTop = null;
          window.setTimeout(() => syncOpenState({ allowMeasure: true }), 250);
        }, { passive: true });

        mobileQuery.addEventListener?.('change', () => syncOpenState({ allowMeasure: true }));
      }

      syncOpenState({ allowMeasure: true });
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', bind, { once: true });
    } else {
      bind();
    }

    document.addEventListener('shopify:section:load', () => {
      frozenTop = null;
      bind();
    });
  })();

(() => {
    const mobileQuery = window.matchMedia('(max-width: 749px)');

    const restoreResults = (predictiveSearch) => {
      if (!mobileQuery.matches || !predictiveSearch.hasAttribute('results')) return;

      const details = predictiveSearch.closest('details-modal.header__search')?.querySelector(':scope > details');
      const input = predictiveSearch.querySelector('input[type="search"]');
      if (!details?.open || !input?.value.trim()) return;

      if (typeof predictiveSearch.open === 'function') {
        predictiveSearch.open();
      } else {
        predictiveSearch.setAttribute('open', 'true');
        input.setAttribute('aria-expanded', 'true');
        predictiveSearch.isOpen = true;
      }
    };

    const bind = (root = document) => {
      root.querySelectorAll('.header details-modal.header__search predictive-search').forEach((predictiveSearch) => {
        if (predictiveSearch.dataset.hederaKeepResultsBound === 'true') return;
        predictiveSearch.dataset.hederaKeepResultsBound = 'true';

        // Dawn closes predictive results whenever the input loses focus. On
        // iPhone, dismissing the software keyboard produces a focusout with no
        // next focused element. Keep the rendered results open in that one
        // mobile-only case; clearing the query or pressing the X still closes it.
        predictiveSearch.addEventListener('focusout', (event) => {
          if (!mobileQuery.matches) return;

          const details = predictiveSearch.closest('details-modal.header__search')?.querySelector(':scope > details');
          const input = predictiveSearch.querySelector('input[type="search"]');
          const nextTarget = event.relatedTarget;
          const keyboardWasDismissed = !nextTarget || nextTarget === document.body || nextTarget === document.documentElement;

          if (!details?.open || !input?.value.trim() || !keyboardWasDismissed) return;

          event.stopImmediatePropagation();
          requestAnimationFrame(() => restoreResults(predictiveSearch));
        }, true);
      });
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => bind(), { once: true });
    } else {
      bind();
    }

    document.addEventListener('shopify:section:load', (event) => bind(event.target));
  })();


/* Footer navigation helpers. */
(() => {
  const openHeaderSearch = () => {
    window.setTimeout(() => {
      const utility = document.querySelector('.header details-modal.header__search--utility > details');
      const candidates = Array.from(
        document.querySelectorAll('.header details-modal.header__search > details')
      );
      const details = candidates.find((item) => {
        const host = item.closest('details-modal');
        return host && window.getComputedStyle(host).display !== 'none';
      }) || utility || candidates[0];

      // Search is an on-page modal. Never send a mobile-menu search click to a
      // separate /search page merely because a header layout is changing.
      if (!details) return;

      if (!details.open) {
        details.querySelector(':scope > summary')?.click();
      }

      window.setTimeout(() => {
        details.querySelector('input[type="search"]')?.focus({ preventScroll: true });
      }, 120);
    }, 260);
  };

  window.HederaOpenHeaderSearch = openHeaderSearch;

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-footer-search-trigger]');
    if (!trigger) return;
    event.preventDefault();
    openHeaderSearch();
  });
})();

/*
  HEDERA Shopify Markets localization.
  Uses Shopify browsing-context suggestions for country/Market only, then
  submits Shopify's native localization form. Language stays user-controlled
  and manual country choices always win.
*/
(() => {
  const COUNTRY_MANUAL_KEY = 'hedera_manual_country_v5';
  const AUTO_ATTEMPT_KEY = 'hedera_geo_auto_attempt_v5';
  const AUTO_APPLIED_KEY = 'hedera_geo_auto_applied_v5';

  const safeStorage = {
    get(storage, key) { try { return storage.getItem(key); } catch (_) { return null; } },
    set(storage, key, value) { try { storage.setItem(key, value); } catch (_) {} },
    remove(storage, key) { try { storage.removeItem(key); } catch (_) {} },
  };

  const flagEmoji = (isoCode) => {
    const code = String(isoCode || '').trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(code)) return '';
    return String.fromCodePoint(...[...code].map((char) => 127397 + char.charCodeAt(0)));
  };

  const currencyDisplaySymbol = (currencyCode, liquidSymbol = '') => {
    const code = String(currencyCode || '').trim().toUpperCase();
    const explicit = {
      GBP: '£', EUR: '€', USD: '$', CAD: 'C$', AUD: 'A$', NZD: 'NZ$',
      RON: 'lei', PLN: 'zł', CZK: 'Kč', HUF: 'Ft', BGN: 'лв',
      SEK: 'kr', NOK: 'kr', DKK: 'kr', TRY: '₺', JPY: '¥', CNY: '¥',
      KRW: '₩', INR: '₹', BRL: 'R$', MXN: 'MX$', ZAR: 'R', ILS: '₪',
      AED: 'د.إ', SAR: 'ر.س'
    };
    if (explicit[code]) return explicit[code];
    const supplied = String(liquidSymbol || '').trim();
    if (supplied && supplied.toUpperCase() !== code) return supplied;
    try {
      const parts = new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency', currency: code, currencyDisplay: 'narrowSymbol', maximumFractionDigits: 0
      }).formatToParts(0);
      const part = parts.find((item) => item.type === 'currency');
      if (part?.value) return part.value;
    } catch (_) {}
    return supplied || code;
  };

  const decorateCountrySelectors = () => {
    document.querySelectorAll('[data-hedera-country-select]').forEach((select) => {
      const countryOnly = select.classList.contains('hedera-country-select--country_only');
      const compact = select.classList.contains('hedera-country-select--compact');
      Array.from(select.options).forEach((option) => {
        const iso = option.dataset.countryIso || option.value;
        const name = option.dataset.countryName || option.textContent.trim();
        const currencyCode = option.dataset.currencyCode || '';
        const currencySymbol = currencyDisplaySymbol(currencyCode, option.dataset.currencySymbol || '');
        const flag = flagEmoji(iso);
        option.textContent = countryOnly
          ? `${flag} ${name}`.trim()
          : compact
            ? `${flag} ${currencySymbol}`.trim()
            : `${flag} ${name} · ${currencySymbol}`.trim();
      });
    });
  };

  const currentReturnTo = () => `${window.location.pathname}${window.location.search}${window.location.hash}`;
  const setReturnTo = (form) => {
    if (!form) return;
    let input = form.querySelector('input[name="return_to"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'return_to';
      form.appendChild(input);
    }
    input.value = currentReturnTo();
  };

  document.addEventListener('change', (event) => {
    const countrySelect = event.target.closest('[data-hedera-country-select]');
    if (countrySelect) {
      safeStorage.set(localStorage, COUNTRY_MANUAL_KEY, String(countrySelect.value || 'manual'));
      safeStorage.remove(sessionStorage, AUTO_ATTEMPT_KEY);
      safeStorage.remove(sessionStorage, AUTO_APPLIED_KEY);
      if (countrySelect.form) {
        setReturnTo(countrySelect.form);
        countrySelect.form.submit();
      }
      return;
    }

    const languageSelect = event.target.closest('[data-hedera-language-select]');
    if (!languageSelect) return;
    safeStorage.remove(sessionStorage, AUTO_ATTEMPT_KEY);
    safeStorage.remove(sessionStorage, AUTO_APPLIED_KEY);
    if (languageSelect.form) {
      setReturnTo(languageSelect.form);
      languageSelect.form.submit();
    }
  }, true);

  const normalise = (value) => String(value || '').trim();
  const normaliseCountry = (value) => normalise(value).toUpperCase();
  const normaliseLanguage = (value) => normalise(value).toLowerCase();

  const submitLocalization = (form, countryInput, languageInput, countryCode, languageCode, markAutoAttempt = true) => {
    const signature = `${countryCode || ''}|${languageCode || ''}`;
    const now = Date.now();
    const previous = safeStorage.get(sessionStorage, AUTO_ATTEMPT_KEY);
    if (previous) {
      try {
        const attempt = JSON.parse(previous);
        if (attempt.signature === signature && now - Number(attempt.time || 0) < 20000) return;
      } catch (_) {}
    }

    if (markAutoAttempt) safeStorage.set(sessionStorage, AUTO_ATTEMPT_KEY, JSON.stringify({ signature, time: now }));
    if (countryCode && countryInput) countryInput.value = countryCode;
    if (languageCode && languageInput) languageInput.value = languageCode;
    setReturnTo(form);
    form.submit();
  };

  const localize = async () => {
    decorateCountrySelectors();

    const context = document.querySelector('[data-hedera-localization-context]');
    const form = document.getElementById('HederaIpLocalizationForm');
    const countryInput = form?.querySelector('[data-hedera-ip-country-input]');
    const languageInput = form?.querySelector('[data-hedera-ip-language-input]');
    if (!context || !form) return;

    const currentCountry = normaliseCountry(context.dataset.currentCountry || window.Shopify?.country);
    const currentLanguage = normaliseLanguage(context.dataset.currentLanguage || window.Shopify?.locale || window.Shopify?.language);
    const currentCurrency = normaliseCountry(context.dataset.currentCurrency || '');
    const renderedCurrency = normaliseCountry(window.Shopify?.currency?.active || context.dataset.renderedCurrency || '');
    const availableCountries = normalise(context.dataset.availableCountries).split(',').map(normaliseCountry).filter(Boolean);
    const countryCurrencies = Object.fromEntries(
      normalise(context.dataset.countryCurrencies).split(',').map((pair) => pair.split(':')).filter((pair) => pair.length === 2).map(([country, currency]) => [normaliseCountry(country), normaliseCountry(currency)])
    );
    const expectedCurrency = countryCurrencies[currentCountry] || currentCurrency;
    const allowCountry = availableCountries.length > 1 && !safeStorage.get(localStorage, COUNTRY_MANUAL_KEY);
    // Country/Market can follow Shopify's GeoIP suggestion, but language should
    // never change itself. Automatic locale switching was a major source of
    // confusing currency/language state and is now strictly opt-in via selector.

    // Shopify can occasionally retain a previous presentment/cart currency after
    // the country context has already changed (privacy browsers make this easier
    // to reproduce). Re-submit the CURRENT country once so country + currency are
    // synchronized before doing GeoIP discovery. The localization form is the
    // supported Shopify mechanism for choosing the country's currency.
    const currencyMismatch = Boolean(
      currentCountry && expectedCurrency && renderedCurrency && expectedCurrency !== renderedCurrency
    );
    if (currencyMismatch && countryInput) {
      const repairKey = `hedera_currency_repair_v2:${currentCountry}:${expectedCurrency}:${renderedCurrency}`;
      if (!safeStorage.get(sessionStorage, repairKey)) {
        safeStorage.set(sessionStorage, repairKey, String(Date.now()));
        safeStorage.remove(sessionStorage, AUTO_APPLIED_KEY);
        submitLocalization(form, countryInput, languageInput, currentCountry, currentLanguage, false);
        return;
      }
    }

    if (!allowCountry) return;

    const currentSignature = `${currentCountry}|${currentLanguage}`;
    const previous = safeStorage.get(sessionStorage, AUTO_ATTEMPT_KEY);
    if (previous) {
      try {
        const attempt = JSON.parse(previous);
        if (attempt.signature === currentSignature) {
          safeStorage.set(sessionStorage, AUTO_APPLIED_KEY, currentSignature);
          safeStorage.remove(sessionStorage, AUTO_ATTEMPT_KEY);
        }
      } catch (_) { safeStorage.remove(sessionStorage, AUTO_ATTEMPT_KEY); }
    }

    // Once Shopify has successfully applied the IP/browser suggestion, don't
    // repeatedly query and redirect on every page in the same browser session.
    if (safeStorage.get(sessionStorage, AUTO_APPLIED_KEY) === currentSignature) return;

    try {
      const root = window.Shopify?.routes?.root || '/';
      const params = new URLSearchParams();
      if (allowCountry) {
        params.set('country[enabled]', 'true');
        if (currentCountry) params.set('country[exclude]', currentCountry);
      }
      const response = await fetch(`${root}browsing_context_suggestions.json?${params.toString()}`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store',
      });
      if (!response.ok) return;

      const payload = await response.json();
      const suggestion = Array.isArray(payload?.suggestions) ? payload.suggestions[0] : null;
      const suggestedCountry = normaliseCountry(
        suggestion?.parts?.country?.handle ||
        suggestion?.parts?.country?.iso_code ||
        payload?.detected_values?.country?.handle ||
        payload?.detected_values?.country?.iso_code
      );
      let targetCountry = currentCountry;
      if (suggestedCountry && availableCountries.includes(suggestedCountry)) {
        targetCountry = suggestedCountry;
      }

      const countryChanged = targetCountry && targetCountry !== currentCountry;
      if (!countryChanged) {
        safeStorage.set(sessionStorage, AUTO_APPLIED_KEY, currentSignature);
        safeStorage.remove(sessionStorage, AUTO_ATTEMPT_KEY);
        return;
      }

      submitLocalization(
        form,
        countryInput,
        languageInput,
        targetCountry,
        currentLanguage
      );
    } catch (_) {
      // Leave the current Shopify Market active if localization discovery fails.
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', localize, { once: true });
  else localize();
  document.addEventListener('shopify:section:load', decorateCountrySelectors);
})();

/* Localise the free-shipping amount in the top ticker from the GBP base. */
(() => {
  const fallbackRates = {
    GBP: 1, EUR: 1.15, USD: 1.34, CAD: 1.84, AUD: 2.06, NZD: 2.25,
    CHF: 1.07, SEK: 12.9, DKK: 8.6, NOK: 13.8, PLN: 4.9, CZK: 28.3,
    HUF: 455, RON: 5.85, BGN: 2.25, TRY: 55, JPY: 198, CNY: 9.6,
    HKD: 10.5, SGD: 1.72, INR: 117, KRW: 1840, BRL: 7.25, MXN: 25.0,
    ZAR: 23.8, ILS: 4.45, AED: 4.92, SAR: 5.03, MYR: 5.65, THB: 43.5,
    IDR: 21900, PHP: 78, TWD: 43.5, VND: 35000, ISK: 166, UAH: 56,
    RSD: 137, BAM: 2.25, MKD: 70.5
  };

  // Some browsers intentionally render less-familiar currencies as ISO codes
  // (for example "PLN 730") when the storefront language is English. For the
  // announcement banner we explicitly use the familiar local currency mark.
  const displaySymbols = {
    GBP: '£', EUR: '€', USD: '$', CAD: 'C$', AUD: 'A$', NZD: 'NZ$',
    CHF: 'CHF', PLN: 'zł', RON: 'lei', CZK: 'Kč', HUF: 'Ft', BGN: 'лв',
    SEK: 'kr', NOK: 'kr', DKK: 'kr', TRY: '₺', JPY: '¥', CNY: '¥',
    HKD: 'HK$', SGD: 'S$', INR: '₹', KRW: '₩', BRL: 'R$', MXN: 'MX$',
    ZAR: 'R', ILS: '₪', AED: 'د.إ', SAR: 'ر.س', MYR: 'RM', THB: '฿',
    IDR: 'Rp', PHP: '₱', TWD: 'NT$', VND: '₫', ISK: 'kr', UAH: '₴',
    RSD: 'дин.', BAM: 'KM', MKD: 'ден.'
  };

  // These currencies are conventionally written after the amount. Keeping this
  // independent of the page language prevents an English locale from turning
  // "730 zł" back into "PLN 730".
  const suffixCurrencies = new Set([
    'PLN', 'RON', 'CZK', 'HUF', 'BGN', 'SEK', 'NOK', 'DKK', 'ISK',
    'RSD', 'BAM', 'MKD'
  ]);

  const currencySymbol = (currency, liquidSymbol = '') => {
    if (displaySymbols[currency]) return displaySymbols[currency];
    const supplied = String(liquidSymbol || '').trim();
    if (supplied && supplied.toUpperCase() !== currency) return supplied;
    try {
      const parts = new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency', currency, currencyDisplay: 'narrowSymbol', maximumFractionDigits: 0
      }).formatToParts(0);
      const part = parts.find((item) => item.type === 'currency');
      if (part?.value && part.value.toUpperCase() !== currency) return part.value;
    } catch (_) {}
    return supplied || currency;
  };

  const formatAmount = (amount, currency, liquidSymbol = '') => {
    const symbol = currencySymbol(currency, liquidSymbol);
    let number = String(Math.round(amount));
    try {
      number = new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0
      }).format(amount);
    } catch (_) {}
    return suffixCurrencies.has(currency) ? `${number} ${symbol}` : `${symbol}${number}`;
  };

  const update = () => {
    document.querySelectorAll('[data-hedera-free-shipping-banner]').forEach((node) => {
      const base = Number(node.dataset.baseGbp || 150);
      const currency = String(window.Shopify?.currency?.active || node.dataset.currency || 'GBP').toUpperCase();
      const liquidSymbol = node.dataset.currencySymbol || '';
      const shopifyRate = Number(window.Shopify?.currency?.rate);
      const rate = Number.isFinite(shopifyRate) && shopifyRate > 0 ? shopifyRate : (fallbackRates[currency] || 1);
      const amount = Math.round(base * rate);
      const amountNode = node.querySelector('[data-hedera-free-shipping-amount]');
      if (amountNode) amountNode.textContent = formatAmount(amount, currency, liquidSymbol);
    });
  };

  window.HederaUpdateShippingAmounts = update;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', update, { once: true });
  else update();
  document.addEventListener('shopify:section:load', update);
})();

/* Keep selected hero content centred in the visible viewport. */
(() => {
  const desktopQuery = window.matchMedia('(min-width: 750px)');
  let frame = 0;

  const getViewportHeroes = () => Array.from(
    document.querySelectorAll('slideshow-component[data-hedera-viewport-center="true"]')
  );

  const updateViewportHeroes = () => {
    frame = 0;
    if (!desktopQuery.matches) {
      getViewportHeroes().forEach((component) => {
        component.querySelectorAll('.slideshow__text').forEach((content) => {
          content.style.removeProperty('--hedera-hero-content-y');
        });
      });
      return;
    }

    const viewportCenter = window.innerHeight / 2;

    getViewportHeroes().forEach((component) => {
      component.querySelectorAll('.slideshow__slide').forEach((slide) => {
        const content = slide.querySelector('.slideshow__text');
        if (!content || slide.offsetParent === null) return;

        const rect = slide.getBoundingClientRect();
        const contentHeight = content.offsetHeight || 0;
        const edgeRoom = Math.max(24, (contentHeight / 2) + 12);
        const maxY = Math.max(edgeRoom, rect.height - edgeRoom);

        // Convert the viewport's physical centre into a Y coordinate inside
        // the hero. Clamping makes the content release naturally at each end.
        const desiredY = viewportCenter - rect.top;
        const localY = Math.min(maxY, Math.max(edgeRoom, desiredY));
        content.style.setProperty('--hedera-hero-content-y', `${localY}px`);
      });
    });
  };

  const fitMobileHeroText = () => {
    document.querySelectorAll('slideshow-component[data-hedera-fit-mobile-text="true"] .banner__text span').forEach((span) => {
      // Remove a previous auto-fit before measuring again after a resize/editor update.
      span.style.removeProperty('font-size');
      if (desktopQuery.matches || span.offsetParent === null) return;

      const container = span.closest('.banner__box') || span.parentElement;
      if (!container) return;
      const available = Math.max(0, container.clientWidth - 24);
      const naturalWidth = span.scrollWidth;
      if (!available || naturalWidth <= available) return;

      const naturalSize = parseFloat(window.getComputedStyle(span).fontSize) || 16;
      const fittedSize = Math.max(12, naturalSize * (available / naturalWidth));
      span.style.setProperty('font-size', `${fittedSize}px`, 'important');
    });
  };

  const requestUpdate = () => {
    if (frame) return;
    frame = window.requestAnimationFrame(() => {
      updateViewportHeroes();
      fitMobileHeroText();
    });
  };

  const initialise = () => {
    requestUpdate();
    // Images can change the adaptive hero height after DOMContentLoaded.
    window.setTimeout(requestUpdate, 120);
    window.setTimeout(requestUpdate, 500);
  };

  window.addEventListener('scroll', requestUpdate, { passive: true });
  window.addEventListener('resize', requestUpdate, { passive: true });
  if (desktopQuery.addEventListener) desktopQuery.addEventListener('change', requestUpdate);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialise, { once: true });
  } else {
    initialise();
  }

  document.addEventListener('shopify:section:load', initialise);
  document.addEventListener('shopify:block:select', requestUpdate);
})();

/* Mobile utility navigation: close the menu first. Search opens the existing
   header search on the current page instead of navigating to /search. */
(() => {
  if (window.__hederaMobileUtilityBound) return;
  window.__hederaMobileUtilityBound = true;

  const closeDrawer = (link) => {
    const drawer = link.closest('header-drawer');
    if (!drawer) return;
    const summary = drawer.querySelector('.header__icon--menu');
    if (typeof drawer.closeMenuDrawer === 'function') {
      drawer.closeMenuDrawer(new Event('click'), summary || false);
    } else {
      const details = drawer.querySelector('#Details-menu-drawer-container') || drawer.querySelector(':scope > details');
      if (details) {
        details.classList.remove('menu-opening');
        details.removeAttribute('open');
      }
      document.body.classList.remove('overflow-hidden-tablet', 'hedera-mobile-menu-open');
    }
  };

  document.addEventListener('click', (event) => {
    const searchLink = event.target.closest('[data-hedera-mobile-search]');
    if (searchLink) {
      event.preventDefault();
      closeDrawer(searchLink);
      window.setTimeout(() => {
        if (typeof window.HederaOpenHeaderSearch === 'function') window.HederaOpenHeaderSearch();
      }, 90);
      return;
    }

    const link = event.target.closest('.hedera-mobile-nav-close');
    if (!link) return;
    closeDrawer(link);
  });
})();

/* HEDERA gallery navigation: close the mobile drawer cleanly before scrolling
   to the homepage lookbook when the user is already on the home page. */
(() => {
  if (window.__hederaGalleryNavBound) return;
  window.__hederaGalleryNavBound = true;

  document.addEventListener('click', (event) => {
    const link = event.target.closest('.hedera-gallery-nav-link');
    if (!link) return;

    let targetUrl;
    try {
      targetUrl = new URL(link.href, window.location.href);
    } catch (_) {
      return;
    }

    if (targetUrl.hash !== '#hedera-gallery' || targetUrl.pathname !== window.location.pathname) return;
    const gallery = document.querySelector('#hedera-gallery');
    if (!gallery) return;

    event.preventDefault();
    const drawer = link.closest('header-drawer');
    if (drawer && typeof drawer.closeMenuDrawer === 'function') {
      const summary = drawer.querySelector('.header__icon--menu');
      drawer.closeMenuDrawer(event, summary || false);
    }

    requestAnimationFrame(() => {
      gallery.scrollIntoView({ behavior: 'smooth', block: 'start' });
      history.replaceState(null, '', '#hedera-gallery');
    });
  });
})();

/* HEDERA commerce & conversion behaviour — consolidated 27 Aug 2026. */
(function(){
  'use strict';

  function q(sel, root){ return (root || document).querySelector(sel); }
  function qa(sel, root){ return Array.from((root || document).querySelectorAll(sel)); }
  function nextFrame(fn){ window.requestAnimationFrame(function(){ window.requestAnimationFrame(fn); }); }

  function initSizeGuides(root){
    qa('[data-hedera-size-guide]', root).forEach(function(wrap){
      if (wrap.dataset.hederaReady === 'true') return;
      wrap.dataset.hederaReady = 'true';

      var modal = q('[data-hedera-size-guide-modal]', wrap);
      var opener = q('[data-hedera-size-guide-open]', wrap);
      if (!modal || !opener) return;

      // Product information can live inside transformed/sticky theme wrappers.
      // A fixed dialog inside one of those ancestors is no longer viewport-fixed,
      // which caused the old mobile guide to appear offset and clipped. Portal the
      // dialog directly to <body> so it is always sized against the real viewport.
      if (modal.parentNode !== document.body) document.body.appendChild(modal);

      var panel = q('[data-hedera-size-guide-panel]', modal);
      var previousFocus = null;
      var isOpen = false;

      function focusableItems(){
        return qa('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])', panel || modal)
          .filter(function(el){ return !el.hidden && el.offsetParent !== null; });
      }

      function open(){
        if (isOpen) return;
        isOpen = true;
        previousFocus = document.activeElement;
        modal.hidden = false;
        modal.setAttribute('aria-hidden', 'false');
        opener.setAttribute('aria-expanded', 'true');
        document.documentElement.classList.add('hedera-modal-open');
        document.body.classList.add('hedera-modal-open');
        nextFrame(function(){
          var closeButton = q('.hedera-size-guide-close', modal);
          if (closeButton) closeButton.focus({ preventScroll:true });
          else if (panel) panel.focus({ preventScroll:true });
        });
      }

      function close(){
        if (!isOpen && modal.hidden) return;
        isOpen = false;
        modal.hidden = true;
        modal.setAttribute('aria-hidden', 'true');
        opener.setAttribute('aria-expanded', 'false');
        document.documentElement.classList.remove('hedera-modal-open');
        document.body.classList.remove('hedera-modal-open');
        if (previousFocus && previousFocus.focus) {
          try { previousFocus.focus({ preventScroll:true }); } catch(_) { previousFocus.focus(); }
        }
      }

      opener.addEventListener('click', open);
      qa('[data-hedera-size-guide-close]', modal).forEach(function(btn){
        btn.addEventListener('click', function(event){ event.preventDefault(); close(); });
      });

      modal.addEventListener('keydown', function(event){
        if (event.key === 'Escape') {
          event.preventDefault();
          close();
          return;
        }
        if (event.key !== 'Tab' || !isOpen) return;
        var items = focusableItems();
        if (!items.length) {
          event.preventDefault();
          if (panel) panel.focus();
          return;
        }
        var first = items[0];
        var last = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });

      // Keep a reference so Shopify section unloads do not leave a portaled
      // dialog behind in the document body while editing the theme.
      wrap._hederaSizeGuideModal = modal;
      wrap._hederaSizeGuideClose = close;
    });
  }


  /* Cart drawer state repair --------------------------------------------------
     The upstream drawer renderer replaces the cart HTML after an AJAX add, but
     it does not reliably remove `is-empty` from the <cart-drawer> host after
     the final item has previously been deleted. Base theme CSS intentionally
     hides the drawer header/body/footer while that class is present, so the
     result is a completely white, apparently frozen cart even though the new
     line item was added successfully. This affected desktop and, because the
     mobile cart is full-screen, could cover the entire mobile viewport.

     Keep the host state in lock-step with the fresh Shopify section HTML for
     every render path (product form, quick add, drawer refresh, etc.). */
  function getIncomingCartEmptyState(payload){
    if (!payload) return null;
    if (typeof payload.item_count === 'number') return payload.item_count === 0;

    var sectionHtml = payload.sections && payload.sections['cart-drawer'];
    if (!sectionHtml) return null;
    try {
      var doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
      var incomingDrawer = doc.querySelector('cart-drawer');
      if (!incomingDrawer) return null;
      return incomingDrawer.classList.contains('is-empty');
    } catch (_) {
      return null;
    }
  }

  function syncCartDrawerEmptyState(drawer, payload){
    if (!drawer) return;
    var empty = getIncomingCartEmptyState(payload);

    // If no response payload is available, recover from the live DOM. This is
    // useful after browser back/forward cache restores and third-party cart
    // mutations that bypass Shopify's normal renderContents payload.
    if (empty === null) {
      if (drawer.querySelector('.cart-drawer-item')) empty = false;
      else if (drawer.querySelector('.drawer__inner-empty')) empty = true;
    }
    if (empty === null) return;

    drawer.classList.toggle('is-empty', empty);
    var items = drawer.querySelector('cart-drawer-items');
    if (items) items.classList.toggle('is-empty', empty);
  }

  function installCartDrawerStateRepair(){
    if (document.documentElement.dataset.hederaCartStateRepair === 'true') return;

    var CartDrawerCtor = window.customElements && customElements.get('cart-drawer');
    if (!CartDrawerCtor || !CartDrawerCtor.prototype) return;
    var proto = CartDrawerCtor.prototype;
    if (proto.__hederaStateRepairInstalled) return;

    var originalRenderContents = proto.renderContents;
    if (typeof originalRenderContents === 'function') {
      proto.renderContents = function(payload){
        // Remove the stale empty state before the theme mutates the DOM, then
        // assert it again afterwards. The second sync also covers empty-cart
        // responses and any theme/app code that changes classes while rendering.
        syncCartDrawerEmptyState(this, payload);
        var result = originalRenderContents.apply(this, arguments);
        syncCartDrawerEmptyState(this, payload);
        return result;
      };
    }

    var originalOpen = proto.open;
    if (typeof originalOpen === 'function') {
      proto.open = function(){
        // Last-line self-heal: never open a non-empty drawer under stale
        // `is-empty` CSS, even if an app updated its contents independently.
        syncCartDrawerEmptyState(this, null);
        return originalOpen.apply(this, arguments);
      };
    }

    proto.__hederaStateRepairInstalled = true;
    document.documentElement.dataset.hederaCartStateRepair = 'true';
    syncCartDrawerEmptyState(q('cart-drawer'), null);
  }

  function sectionInnerHTML(sectionHtml, selector){
    if (!sectionHtml) return '';
    try {
      var doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
      var node = selector ? doc.querySelector(selector) : doc.querySelector('.shopify-section');
      return node ? node.innerHTML : '';
    } catch(_){
      return '';
    }
  }

  function updateHeaderCartIcon(sectionHtml){
    var target = document.getElementById('cart-icon-bubble');
    if (!target || !sectionHtml) return false;
    var html = sectionInnerHTML(sectionHtml, '.shopify-section');
    if (!html) return false;
    target.innerHTML = html;
    return true;
  }

  function updateHeaderCartCountFallback(count){
    var target = document.getElementById('cart-icon-bubble');
    if (!target || typeof count !== 'number') return;
    var bubble = target.querySelector('.cart-count-bubble');
    if (count <= 0) {
      if (bubble) bubble.remove();
      return;
    }
    if (!bubble) {
      bubble = document.createElement('div');
      bubble.className = 'cart-count-bubble';
      target.appendChild(bubble);
    }
    bubble.innerHTML = '<span aria-hidden="true">' + (count < 100 ? count : '') + '</span>' +
      '<span class="visually-hidden">' + count + ' items in cart</span>';
  }

  function markCartDrawerDirty(){
    document.documentElement.dataset.hederaCartDrawerDirty = 'true';
  }

  function clearCartDrawerDirty(){
    delete document.documentElement.dataset.hederaCartDrawerDirty;
  }

  function isCartDrawerDirty(){
    return document.documentElement.dataset.hederaCartDrawerDirty === 'true';
  }

  var cartRefreshPromise = null;
  function refreshCartDrawerForIntent(){
    if (cartRefreshPromise) return cartRefreshPromise;
    var cart = q('cart-drawer');
    if (!cart) return Promise.resolve(false);

    var body = JSON.stringify({
      updates: {},
      sections: ['cart-drawer', 'cart-icon-bubble'],
      sections_url: window.location.pathname + window.location.search
    });

    cartRefreshPromise = fetch((window.routes && window.routes.cart_update_url) || '/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: body,
      credentials: 'same-origin'
    }).then(function(response){
      if (!response.ok) throw new Error('Could not refresh the bag.');
      return response.json();
    }).then(function(data){
      if (data.sections && data.sections['cart-icon-bubble']) {
        updateHeaderCartIcon(data.sections['cart-icon-bubble']);
      }
      if (typeof cart.renderContents === 'function' && data.sections) {
        cart.renderContents(data, false);
      } else if (typeof cart.open === 'function') {
        cart.open();
      } else {
        window.location.assign((window.routes && window.routes.cart_url) || '/cart');
      }
      clearCartDrawerDirty();
      return true;
    }).catch(function(error){
      console.error('[HEDERA cart refresh]', error);
      window.location.assign((window.routes && window.routes.cart_url) || '/cart');
      return false;
    }).finally(function(){
      cartRefreshPromise = null;
    });

    return cartRefreshPromise;
  }

  function addVariantToCart(variantId, sourceButton){
    var formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    // Quick Add is deliberately quiet: update the header badge only. The cart
    // drawer is refreshed later, when the customer intentionally opens it.
    formData.append('sections', 'cart-icon-bubble');
    formData.append('sections_url', window.location.pathname + window.location.search);

    if (sourceButton) sourceButton.setAttribute('aria-busy', 'true');
    return fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json' },
      body: formData,
      credentials: 'same-origin'
    }).then(function(response){
      return response.json().then(function(data){
        if (!response.ok || data.status) throw new Error(data.description || data.message || 'Could not add this size.');
        return data;
      });
    }).then(function(data){
      var updated = data.sections && updateHeaderCartIcon(data.sections['cart-icon-bubble']);
      markCartDrawerDirty();

      var badgePromise = Promise.resolve();
      if (!updated) {
        // Very old/modified Shopify runtimes can omit bundled sections. Keep
        // the badge accurate without opening the cart by reading /cart.js.
        badgePromise = fetch((window.routes && window.routes.cart_url ? window.routes.cart_url + '.js' : '/cart.js'), {
          headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          credentials: 'same-origin'
        }).then(function(cartDataResponse){
          if (!cartDataResponse.ok) return;
          return cartDataResponse.json().then(function(cartData){
            updateHeaderCartCountFallback(Number(cartData.item_count || 0));
          });
        }).catch(function(){});
      }

      return badgePromise.then(function(){
        if (document.documentElement.dataset.hederaQuickAddOpenCart === 'true') {
          return refreshCartDrawerForIntent().then(function(){ return data; });
        }
        return data;
      });
    }).finally(function(){
      if (sourceButton) sourceButton.removeAttribute('aria-busy');
    });
  }

  function initQuickAdd(){
    if (document.documentElement.dataset.hederaQuickAddReady === 'true') return;
    document.documentElement.dataset.hederaQuickAddReady = 'true';

    // Quick Add keeps customers on the product grid. If the cart drawer has
    // become stale, only refresh/open it when the customer deliberately taps
    // the cart icon. Capture phase prevents the theme from opening stale
    // empty-drawer markup first.
    document.addEventListener('click', function(event){
      var cartLink = event.target && event.target.closest ? event.target.closest('#cart-icon-bubble') : null;
      if (!cartLink || !isCartDrawerDirty() || !q('cart-drawer')) return;
      event.preventDefault();
      event.stopPropagation();
      refreshCartDrawerForIntent();
    }, true);

    document.addEventListener('submit', function(event){
      var form = event.target && event.target.closest ? event.target.closest('[data-hedera-quick-add-form]') : null;
      if (!form) return;

      var wrap = form.closest('[data-hedera-card-quick-add]');
      var btn = q('[data-hedera-quick-size]', form);
      var idInput = q('input[name="id"]', form);
      var id = (idInput && idInput.value) || (btn && btn.getAttribute('data-variant-id'));
      if (!wrap || !btn || !id || btn.disabled || wrap.classList.contains('is-loading')) return;

      // JS enhances the real Shopify form. If this script is unavailable, the
      // browser still submits the same variant to Shopify normally.
      event.preventDefault();
      // Do not stop propagation: CRM/analytics/app listeners can still observe
      // the buyer's Quick Add intent while HEDERA performs the AJAX request.
      qa('[data-hedera-quick-size]', wrap).forEach(function(sizeButton){
        var selected = sizeButton === btn;
        sizeButton.classList.toggle('is-selected', selected);
        sizeButton.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
      wrap.classList.add('is-loading');
      wrap.classList.remove('is-error', 'is-added');
      var status = q('[data-hedera-quick-add-status]', wrap);
      if(status) status.textContent = 'Adding…';

      addVariantToCart(id, btn).then(function(){
        wrap.classList.add('is-added');
        if(status) status.textContent = 'Added ✓';
        window.setTimeout(function(){
          wrap.classList.remove('is-added');
          if(status) status.textContent = '';
        }, 1300);
      }).catch(function(error){
        console.error('[HEDERA quick add]', error);
        wrap.classList.add('is-error');
        if(status) status.textContent = 'Opening the bag…';

        // Last-resort reliability path: the Quick Add control is a genuine
        // Shopify /cart/add form, so a native submit still adds the exact
        // variant if an app, browser extension or theme script blocks AJAX.
        // Use the prototype method so no other submit listener can intercept it.
        window.setTimeout(function(){
          try {
            HTMLFormElement.prototype.submit.call(form);
          } catch (_) {
            if(status) status.textContent = error && error.message ? error.message : 'Please try again.';
          }
        }, 80);
      }).finally(function(){
        wrap.classList.remove('is-loading');
      });
    }, true);
  }

  function getMainProduct(root){
    if (root && root.matches && root.matches('[id^="MainProduct-"]')) return root;
    return q('[id^="MainProduct-"]', root || document) || q('[id^="MainProduct-"]');
  }

  function findSizeGroup(main){
    return qa('variant-selects .product-form__input', main).find(function(group){
      var label = q('.form__label', group);
      return label && /size/i.test(label.textContent || '');
    });
  }

  function selectedOptions(variantSelects){
    return qa(':scope > .product-form__input', variantSelects).map(function(group){
      var type = q('.product-form__input__type', group);
      var typeName = type ? String(type.dataset.type || '').toLowerCase() : '';
      if (typeName === 'dropdown' || typeName === 'quantity-breaks') {
        var select = q('.select__select', group);
        if (select) return select.value;
      }
      var checked = q('input[type="radio"]:checked', group);
      if (checked) return checked.value;
      var fallback = q('select', group);
      return fallback ? fallback.value : '';
    });
  }

  function parseMainVariants(main){
    if (main._hederaVariants) return main._hederaVariants;
    var data = q('[data-hedera-main-variant-data]', main);
    if (!data) return [];
    try { main._hederaVariants = JSON.parse(data.textContent); }
    catch(_) { main._hederaVariants = []; }
    return main._hederaVariants;
  }

  function variantForSelection(main, variantSelects){
    var options = selectedOptions(variantSelects);
    return parseMainVariants(main).find(function(variant){
      if (!variant.options || variant.options.length !== options.length) return false;
      return variant.options.every(function(value, index){ return String(value) === String(options[index]); });
    });
  }

  function cleanMoneyText(value){
    var text = String(value == null ? '' : value);
    if (text.indexOf('<') === -1) return text.trim();
    var holder = document.createElement('div');
    holder.innerHTML = text;
    return (holder.textContent || holder.innerText || '').trim();
  }

  function syncPrice(main, variant){
    var priceRoot = q('[id^="price-"] .price', main);
    if (!priceRoot || !variant) return;
    var priceText = cleanMoneyText(variant.price);
    var compareText = cleanMoneyText(variant.compare_price);
    var onSale = Number(variant.compare_price_cents || 0) > Number(variant.price_cents || 0);
    priceRoot.classList.toggle('price--sold-out', !variant.available);
    priceRoot.classList.toggle('price--on-sale', onSale);
    qa('.price__regular .price-item--regular', priceRoot).forEach(function(el){ el.textContent = priceText; });
    qa('.price__sale .price-item--sale', priceRoot).forEach(function(el){ el.textContent = priceText; });
    qa('.price__compare-price .price-item--regular', priceRoot).forEach(function(el){ el.textContent = compareText; });

    qa('.main-atc-price', main).forEach(function(el){
      el.textContent = variant.available ? (priceText + ' •') : '';
      el.classList.toggle('hidden', !variant.available);
    });
    qa('[id^="sticky-atc-price-"]', document).forEach(function(el){
      if (el.closest('sticky-atc') && el.closest('sticky-atc').dataset.section === main.dataset.section) el.textContent = priceText;
    });
  }

  function syncButtons(main, variant){
    qa('.main-product-atc', main).forEach(function(button){
      if (!variant) return;
      button.disabled = !variant.available;
      button.toggleAttribute('disabled', !variant.available);
      if (!variant.available) button.setAttribute('data-unavailable', 'true');
      else button.removeAttribute('data-unavailable');
      var label = q('.main-atc__label__text', button);
      if (label) label.textContent = variant.available ? ((window.variantStrings && window.variantStrings.addToCart) || 'Add to cart') : ((window.variantStrings && window.variantStrings.soldOut) || 'Sold out');
    });
  }

  function syncVariantInputs(main, variant){
    if (!variant) return;
    var section = main.dataset.section || '';
    var selectors = [
      '#product-form-' + section + ' input[name="id"]',
      '#product-form-installment-' + section + ' input[name="id"]',
      '#ProductForm-' + section + ' input[name="id"]',
      'sticky-atc[data-section="' + section + '"] input[name="id"]',
      'secondary-variant-select[data-section="' + section + '"] input[name="id"]'
    ];
    qa(selectors.join(','), document).forEach(function(input){
      if (String(input.value) !== String(variant.id)) {
        input.value = variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  function syncMedia(main, variant){
    if (!variant || !variant.featured_media_id) return;
    var gallery = q('media-gallery:not([id$="-duplicate"])', main);
    if (!gallery || typeof gallery.setActiveMedia !== 'function') return;
    if (gallery.dataset.desktopLayout === 'stacked' && window.matchMedia('(min-width: 990px)').matches) return;
    try { gallery.setActiveMedia(main.dataset.section + '-' + variant.featured_media_id, true, false, variant); }
    catch(_) {}
  }

  function syncUrl(main, variant){
    if (!variant || !window.history || !window.history.replaceState) return;
    var picker = q('variant-selects', main);
    if (!picker || picker.dataset.updateUrl === 'false') return;
    try {
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    } catch(_) {}
  }

  function updateSelectedSize(main){
    var sizeGroup = findSizeGroup(main);
    if(!sizeGroup) return;
    var label = q('.form__label', sizeGroup);
    if (!label) return;
    var checked = q('input[type="radio"]:checked', sizeGroup);
    var select = q('select', sizeGroup);
    var value = checked ? checked.value : (select ? select.value : '');
    var old = q('.hedera-selected-size', label);
    if(!old){ old = document.createElement('span'); old.className='hedera-selected-size'; label.appendChild(old); }
    old.textContent = value ? ' · ' + value : '';
  }

  function renderInventory(main, variant){
    var assurance = q('[data-hedera-purchase-assurance]', main);
    var output = assurance && q('[data-hedera-stock-message]', assurance);
    if(!output || !variant) return;
    output.innerHTML = '';
    if(!variant.available) return;
    if(variant.inventory_management === 'shopify' && variant.inventory_policy !== 'continue' && variant.inventory_quantity > 0 && variant.inventory_quantity <= 3){
      output.innerHTML = '<span class="hedera-stock-dot" aria-hidden="true"></span>Only ' + variant.inventory_quantity + ' left in this size';
    }
  }

  function syncMainVariant(main, variantSelects, options){
    var variant = variantForSelection(main, variantSelects);
    if (!variant) return;
    syncVariantInputs(main, variant);
    syncButtons(main, variant);
    syncPrice(main, variant);
    renderInventory(main, variant);
    updateSelectedSize(main);
    if (!options || options.media !== false) syncMedia(main, variant);
    if (!options || options.url !== false) syncUrl(main, variant);
    main.dataset.hederaSelectedVariant = String(variant.id);
  }

  function initReliableVariants(root){
    var main = getMainProduct(root);
    if (!main || main.dataset.hederaVariantReady === 'true') return;
    var picker = q('variant-selects', main);
    if (!picker || !parseMainVariants(main).length) return;
    main.dataset.hederaVariantReady = 'true';

    picker.addEventListener('change', function(){
      // Run after the base theme handler so this layer repairs state rather than competing with it.
      window.setTimeout(function(){ syncMainVariant(main, picker); }, 0);
    });

    picker.addEventListener('click', function(event){
      var label = event.target.closest('label[for]');
      if (!label || !picker.contains(label)) return;
      var input = document.getElementById(label.getAttribute('for'));
      if (!input || input.disabled || input.classList.contains('non-existent')) return;
      window.setTimeout(function(){
        // Some versions of the underlying theme occasionally leave the old radio checked.
        // Guarantee the option that was actually clicked becomes the selected option.
        if (!input.checked) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          syncMainVariant(main, picker);
        }
      }, 0);
    });

    syncMainVariant(main, picker, { media: false, url: false });
  }

  function initProductSizeUX(root){
    var main = getMainProduct(root);
    if(!main || main.dataset.hederaSizeReady === 'true') return;
    main.dataset.hederaSizeReady = 'true';
    var sizeGroup = findSizeGroup(main);
    if(!sizeGroup) return;
    sizeGroup.classList.add('hedera-size-picker');
    updateSelectedSize(main);
    sizeGroup.addEventListener('change', function(){ window.setTimeout(function(){ updateSelectedSize(main); }, 0); });

    qa('.main-product-atc', main).forEach(function(btn){
      btn.addEventListener('click', function(e){
        var radioExists = q('input[type="radio"]', sizeGroup);
        var checked = q('input[type="radio"]:checked', sizeGroup);
        var select = q('select', sizeGroup);
        if((radioExists && !checked) || (select && !select.value)){
          e.preventDefault();
          e.stopImmediatePropagation();
          sizeGroup.classList.add('hedera-size-picker--attention');
          sizeGroup.scrollIntoView({behavior:'smooth', block:'center'});
          window.setTimeout(function(){ sizeGroup.classList.remove('hedera-size-picker--attention'); }, 1600);
        }
      }, true);
    });
  }

  function initInventory(root){
    var main = getMainProduct(root);
    if(!main || main.dataset.hederaInventoryReady === 'true') return;
    main.dataset.hederaInventoryReady = 'true';
    var picker = q('variant-selects', main);
    var variants = parseMainVariants(main);
    if(!picker || !variants.length) return;
    var render = function(){
      var selectedId = main.dataset.hederaSelectedVariant;
      var variant = variants.find(function(v){ return String(v.id) === String(selectedId); }) || variantForSelection(main, picker);
      if (variant) renderInventory(main, variant);
    };
    render();
    picker.addEventListener('change', function(){ window.setTimeout(render, 20); });
  }

  function initRecentlyViewed(root){
    var currentEl = q('[data-hedera-current-product]', root || document);
    var section = q('[data-hedera-recently-viewed]', root || document);
    if(!currentEl || !section || section.dataset.hederaReady === 'true') return;
    section.dataset.hederaReady = 'true';

    var current;
    try { current = JSON.parse(currentEl.textContent); } catch(_){ return; }
    if (!current || !current.id) return;

    var currency = String(current.currency || 'GBP').toUpperCase();
    var key = 'hedera-recently-viewed-v2-' + currency;
    var list = [];
    try {
      list = JSON.parse(localStorage.getItem(key) || '[]');
      if(!Array.isArray(list)) list = [];
      // V1 stored theme money HTML in some shops. Do not let that stale markup
      // appear as visible text after the V2 format was installed.
      localStorage.removeItem('hedera-recently-viewed-v1');
    } catch(_){ list = []; }

    var limit = parseInt(section.dataset.maxItems || '4', 10);
    if (!(limit > 0)) limit = 4;
    limit = Math.min(4, limit);
    var previous = list.filter(function(item){ return item && String(item.id) !== String(current.id); }).slice(0, limit);
    var grid = q('[data-hedera-recently-viewed-grid]', section);

    if(previous.length && grid){
      grid.textContent = '';
      previous.forEach(function(item){
        var a = document.createElement('a');
        a.className = 'hedera-rv-card';
        a.href = item.url || '#';
        a.setAttribute('aria-label', item.title ? 'View ' + item.title : 'View product');

        var imageWrap = document.createElement('span');
        imageWrap.className = 'hedera-rv-card__image';
        if (item.image) {
          var img = document.createElement('img');
          img.src = item.image;
          img.alt = item.title || '';
          img.loading = 'lazy';
          img.decoding = 'async';
          imageWrap.appendChild(img);
        }

        var title = document.createElement('span');
        title.className = 'hedera-rv-card__title';
        title.textContent = item.title || '';

        var price = document.createElement('span');
        price.className = 'hedera-rv-card__price';
        var priceText = '';
        if (Number.isFinite(Number(item.priceCents)) && item.currency) {
          try {
            priceText = new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
              style: 'currency',
              currency: String(item.currency).toUpperCase()
            }).format(Number(item.priceCents) / 100);
          } catch (_) {}
        }
        // textContent is intentional: legacy price strings are never interpreted as HTML.
        if (!priceText) priceText = String(item.price || '').replace(/<[^>]*>/g, '').trim();
        price.textContent = priceText;

        a.appendChild(imageWrap);
        a.appendChild(title);
        a.appendChild(price);
        grid.appendChild(a);
      });
      section.dataset.count = String(previous.length);
      section.hidden = false;
    }

    list = [current].concat(previous).slice(0, Math.max(limit + 1, 5));
    try { localStorage.setItem(key, JSON.stringify(list)); } catch(_){}
  }

  function scrollerStep(scroller){
    var card = q('.new-drop-native-item', scroller);
    if (!card) return Math.max(260, scroller.clientWidth * 0.75);
    var styles = window.getComputedStyle(q('.new-drop-native-list', scroller));
    var gap = parseFloat(styles.columnGap || styles.gap || 0) || 0;
    return card.getBoundingClientRect().width + gap;
  }

  function updateScrollerControls(scroller, controls){
    if (!controls) return;
    var max = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    var ratio = max > 0 ? Math.max(0, Math.min(1, scroller.scrollLeft / max)) : 0;
    var track = q('.hedera-new-drop-control-track', controls);
    var thumb = track && q('span', track);
    if (thumb) {
      var travel = Math.max(0, track.clientWidth - thumb.offsetWidth);
      thumb.style.transform = 'translateX(' + (ratio * travel) + 'px)';
    }
    var prev = q('[data-new-drop-prev]', controls);
    var next = q('[data-new-drop-next]', controls);
    if (prev) prev.disabled = scroller.scrollLeft <= 2;
    if (next) next.disabled = scroller.scrollLeft >= max - 2;
  }

  function initNewDropDrag(root){
    qa('[data-new-drop-scroller]', root).forEach(function(scroller){
      if(scroller.dataset.hederaDragReady==='true') return;
      scroller.dataset.hederaDragReady='true';
      var controls = scroller.parentElement && q('[data-new-drop-controls]', scroller.parentElement);
      var down=false,startX=0,startScroll=0,moved=false;
      scroller.addEventListener('pointerdown',function(e){
        if(e.pointerType==='touch') return;
        // Do not capture the pointer when the customer is using a real control.
        // Capturing pointerdown from the Quick Add size button was preventing
        // the button's normal click/submit sequence on desktop.
        if(e.target && e.target.closest && e.target.closest('a, button, input, select, textarea, label, form, .color-swatch')) { down=false; moved=false; return; }
        down=true; moved=false; startX=e.clientX; startScroll=scroller.scrollLeft;
        scroller.classList.add('is-dragging');
        if(scroller.setPointerCapture) scroller.setPointerCapture(e.pointerId);
      });
      scroller.addEventListener('pointermove',function(e){
        if(!down) return;
        var dx=e.clientX-startX;
        if(Math.abs(dx)>4)moved=true;
        scroller.scrollLeft=startScroll-dx;
      });
      function end(){ down=false; scroller.classList.remove('is-dragging'); }
      scroller.addEventListener('pointerup',end);
      scroller.addEventListener('pointercancel',end);
      scroller.addEventListener('click',function(e){ if(moved){e.preventDefault();e.stopPropagation();moved=false;} },true);
      scroller.addEventListener('scroll', function(){ updateScrollerControls(scroller, controls); }, { passive:true });
      window.addEventListener('resize', function(){ updateScrollerControls(scroller, controls); }, { passive:true });
      if(controls){
        var prev=q('[data-new-drop-prev]',controls), next=q('[data-new-drop-next]',controls);
        if(prev) prev.addEventListener('click',function(){ scroller.scrollBy({left:-scrollerStep(scroller),behavior:'smooth'}); });
        if(next) next.addEventListener('click',function(){ scroller.scrollBy({left:scrollerStep(scroller),behavior:'smooth'}); });
      }
      nextFrame(function(){ updateScrollerControls(scroller, controls); });
    });
  }

  function initMobileGalleryPreload(root){
    if (!window.matchMedia || !window.matchMedia('(max-width: 749px)').matches) return;

    qa('media-gallery[data-hedera-mobile-preload-next]', root || document).forEach(function(gallery){
      if (gallery.dataset.hederaPreloadReady === 'true') return;
      gallery.dataset.hederaPreloadReady = 'true';

      var list = q('.product__media-list', gallery);
      if (!list) return;
      var nextCount = parseInt(gallery.dataset.hederaMobilePreloadNext || '2', 10);
      if (!(nextCount >= 0)) nextCount = 2;
      nextCount = Math.min(3, nextCount);

      function visibleItems(){
        return qa('.product__media-item', list).filter(function(item){
          return !item.classList.contains('hidden') && item.offsetParent !== null;
        });
      }

      function warmImage(img, highPriority){
        if (!img) return;
        img.loading = 'eager';
        img.setAttribute('loading', 'eager');
        try { img.fetchPriority = highPriority ? 'high' : 'low'; } catch(_){}
        if (img.dataset.hederaWarmed === 'true') return;
        img.dataset.hederaWarmed = 'true';

        // Force the browser to begin fetching the same responsive candidate now,
        // while leaving the rest of the gallery lazy-loaded for performance.
        try {
          var preload = new Image();
          preload.decoding = 'async';
          if (img.sizes) preload.sizes = img.sizes;
          if (img.srcset) preload.srcset = img.srcset;
          preload.src = img.currentSrc || img.src;
          window.__hederaImageWarmers = window.__hederaImageWarmers || [];
          window.__hederaImageWarmers.push(preload);
          if (window.__hederaImageWarmers.length > 12) window.__hederaImageWarmers.shift();
        } catch(_){}
      }

      function closestIndex(items){
        if (!items.length) return 0;
        var active = items.findIndex(function(item){ return item.classList.contains('is-active'); });
        if (active >= 0) return active;
        var left = list.getBoundingClientRect().left;
        var best = 0, distance = Infinity;
        items.forEach(function(item, index){
          var d = Math.abs(item.getBoundingClientRect().left - left);
          if (d < distance) { distance = d; best = index; }
        });
        return best;
      }

      function preloadAround(index){
        var items = visibleItems();
        if (!items.length) return;
        index = Math.max(0, Math.min(items.length - 1, index || 0));
        for (var offset = 0; offset <= nextCount; offset += 1) {
          var item = items[index + offset];
          if (!item) break;
          warmImage(q('img', item), offset === 0);
        }
      }

      var scheduled = false;
      function schedule(){
        if (scheduled) return;
        scheduled = true;
        window.requestAnimationFrame(function(){
          scheduled = false;
          var items = visibleItems();
          preloadAround(closestIndex(items));
        });
      }

      preloadAround(0);
      list.addEventListener('scroll', schedule, { passive:true });
      var picker = q('variant-selects', gallery.closest('[id^="MainProduct-"]') || document);
      if (picker) picker.addEventListener('change', function(){ window.setTimeout(schedule, 80); });
    });
  }

  function formatMoneyAmount(amount, currency){
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency: currency || 'GBP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount);
    } catch(_){ return (currency || 'GBP') + ' ' + Math.round(amount); }
  }

  function updateCurrencyAwareCartProgress(root){
    qa('[data-hedera-cart-progress]', root || document).forEach(function(bar){
      var baseGoal = parseFloat(bar.dataset.goalBase || '0');
      var subtotalCents = parseInt(bar.dataset.subtotalCents || '0', 10) || 0;
      var runtimeCurrency = String((window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || bar.dataset.currency || 'GBP').toUpperCase();
      var fallbackRates = { GBP:1, EUR:1.15, USD:1.34, CAD:1.84, AUD:2.06, NZD:2.25, CHF:1.07, SEK:12.9, DKK:8.6, NOK:13.8, PLN:4.9, CZK:28.3, HUF:455, RON:5.85, BGN:2.25, TRY:55, JPY:198, CNY:9.6, HKD:10.5, SGD:1.72, INR:117, KRW:1840, BRL:7.25, MXN:25, ZAR:23.8, ILS:4.45, AED:4.92, SAR:5.03, MYR:5.65, THB:43.5, IDR:21900, PHP:78, TWD:43.5, VND:35000 };
      var rate = Number(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || fallbackRates[runtimeCurrency] || 1;
      if (!(baseGoal > 0) || !(rate > 0)) return;

      // The configured cart goal is stored in the shop base currency (GBP),
      // while Shopify returns cart subtotal cents in the active presentment currency.
      var targetCents = Math.max(1, Math.round(baseGoal * 100 * rate));
      var remainingCents = Math.max(0, targetCents - subtotalCents);
      var reached = subtotalCents >= targetCents;
      var percent = Math.max(0, Math.min(100, subtotalCents / targetCents * 100));
      var message = q('[data-hedera-cart-progress-message]', bar);
      var fill = q('[data-hedera-cart-progress-fill]', bar);
      if (fill) fill.style.width = percent + '%';
      if (message) {
        if (reached) message.textContent = bar.dataset.successTemplate || "You've unlocked FREE UK SHIPPING ✓";
        else {
          var amount = formatMoneyAmount(remainingCents / 100, runtimeCurrency);
          message.textContent = String(bar.dataset.progressTemplate || "You're [amount] away from FREE UK SHIPPING").replace('[amount]', amount);
        }
      }
    });
  }

  function initCurrencyAwareCartProgress(root){
    updateCurrencyAwareCartProgress(root || document);
    if (document.documentElement.dataset.hederaCartProgressObserver === 'true') return;
    document.documentElement.dataset.hederaCartProgressObserver = 'true';
    var drawer = q('cart-drawer');
    if (!drawer || !window.MutationObserver) return;
    var scheduled = false;
    var observer = new MutationObserver(function(mutations){
      var relevant = mutations.some(function(mutation){
        // Ignore the text replacement performed by this updater itself.
        return !(mutation.target && mutation.target.closest && mutation.target.closest('[data-hedera-cart-progress]'));
      });
      if (!relevant || scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(function(){
        scheduled = false;
        updateCurrencyAwareCartProgress(drawer);
        if (typeof window.HederaUpdateShippingAmounts === 'function') window.HederaUpdateShippingAmounts();
      });
    });
    observer.observe(drawer, { childList:true, subtree:true });
  }

  function init(root){
    installCartDrawerStateRepair();
    initSizeGuides(root);
    initQuickAdd(root);
    initReliableVariants(root);
    initProductSizeUX(root);
    initInventory(root);
    initRecentlyViewed(root);
    initNewDropDrag(root);
    initMobileGalleryPreload(root);
    initCurrencyAwareCartProgress(root);
  }

  if(document.readyState==='loading') document.addEventListener('DOMContentLoaded',function(){init(document);},{once:true});
  else init(document);
  document.addEventListener('shopify:section:load',function(e){init(e.target);});
  document.addEventListener('shopify:section:unload',function(e){
    qa('[data-hedera-size-guide]', e.target).forEach(function(wrap){
      if (typeof wrap._hederaSizeGuideClose === 'function') wrap._hederaSizeGuideClose();
      var modal = wrap._hederaSizeGuideModal;
      if (modal && modal.parentNode) modal.parentNode.removeChild(modal);
    });
  });
})();
